/**
 * useCalendarLabels — 캘린더 모델의 동적 라벨을 현재 locale 로 번역하는 훅.
 *
 * eventChipStyle().label / VISIBILITY_LABELS / ATTENDEE_STATUS_LABELS 는
 * 컴포넌트 밖 상수라 <Trans> 로 못 감싼다. lingui 표준 msg + i18n._() 패턴으로
 * 카테고리/상태 → 번역된 string 을 반환한다 (title 속성·화면 텍스트 모두 사용 가능).
 */
import { useLingui } from '@lingui/react';
import { msg } from '@lingui/macro';
import type { MessageDescriptor } from '@lingui/core';
import { EventCategory, AttendeeStatus, EventVisibility, CalendarViewMode } from '../../models/CalendarEvent';

const CATEGORY_MSG: Record<EventCategory, MessageDescriptor> = {
  private: msg`Personal event`,
  private_shared: msg`Shared personal event`,
  channel_mine: msg`My channel event`,
  channel_shared: msg`Shared channel event`,
  received: msg`Shared event`,
  invited: msg`Invited event`,
  workspace: msg`Workspace event`,
  holiday: msg`Holiday`,
  task: msg`Task deadline`,
};

const ATTENDEE_STATUS_MSG: Record<AttendeeStatus, MessageDescriptor> = {
  pending: msg`No response`,
  accepted: msg`Accepted`,
  declined: msg`Declined`,
  tentative: msg`Tentative`,
};

const VISIBILITY_MSG: Record<EventVisibility, MessageDescriptor> = {
  private: msg`Private`,
  public_channels: msg`Shared with channels`,
  public_workspace: msg`Workspace-wide`,
};

const VIEW_MODE_MSG: Record<CalendarViewMode, MessageDescriptor> = {
  month: msg`Month`,
  week: msg`Week`,
  day: msg`Day`,
  // 구글 캘린더식 — agenda(목록) 뷰는 "Schedule"(일정/スケジュール) 로 표시
  agenda: msg`Schedule`,
};

/** 국가 코드 → 플래그 이모지 (regional indicator) */
function flagEmoji(code: string): string {
  if (!/^[A-Za-z]{2}$/.test(code)) return '';
  return code.toUpperCase().replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));
}

export function useCalendarLabels() {
  const { i18n } = useLingui();
  return {
    category: (c: EventCategory) => i18n._(CATEGORY_MSG[c]),
    attendeeStatus: (s: AttendeeStatus) => i18n._(ATTENDEE_STATUS_MSG[s]),
    visibility: (v: EventVisibility) => i18n._(VISIBILITY_MSG[v]),
    viewMode: (v: CalendarViewMode) => i18n._(VIEW_MODE_MSG[v]),
    /** 공휴일 국가명 — 현재 locale 로 자동 표시 (Intl.DisplayNames). 플래그 + 국가명 */
    country: (code: string) => {
      let name = code;
      try {
        const dn = new Intl.DisplayNames([i18n.locale || 'en'], { type: 'region' });
        name = dn.of(code.toUpperCase()) ?? code;
      } catch { /* 미지원 환경 fallback: code */ }
      const flag = flagEmoji(code);
      return flag ? `${flag} ${name}` : name;
    },
  };
}
